/**
 * Aggregated icon definitions — one file per icon for scalability.
 *
 * To add a new icon:
 * 1. Create `<icon-name>.ts` in this directory exporting an IconDef.
 * 2. Import and add it to the `iconDefs` record below.
 * 3. Register the name in `../registry.ts`.
 */

import type { IconName } from '../registry';
import type { IconDef } from '../types';
import { add } from './add';
import { adjusting } from './adjusting';
import { alcohol } from './alcohol';
import { alert } from './alert';
import { attach } from './attach';
import { backward } from './backward';
import { backward10s } from './backward-10s';
import { barGraph } from './bar-graph';
import { beakerFilled } from './beaker-filled';
import { beakerOutlined } from './beaker-outlined';
import { beer } from './beer';
import { blow } from './blow';
import { bolt } from './bolt';
import { boltFilled } from './bolt-filled';
import { bookmark } from './bookmark';
import { books } from './books';
import { bubbleSpeech } from './bubble-speech';
import { calendar } from './calendar';
import { calendarTime } from './calendar-time';
import { camera } from './camera';
import { cannabis } from './cannabis';
import { care } from './care';
import { chart } from './chart';
import { check } from './check';
import { chewingTobacco } from './chewing-tobacco';
import { cigarette } from './cigarette';
import { clock } from './clock';
import { close } from './close';
import { coffee } from './coffee';
import { coin } from './coin';
import { compass } from './compass';
import { computer } from './computer';
import { corporate } from './corporate';
import { cup } from './cup';
import { delivery } from './delivery';
import { detach } from './detach';
import { document } from './document';
import { downArrow } from './down-arrow';
import { downChevron } from './down-chevron';
import { download } from './download';
import { dropdown } from './dropdown';
import { edit } from './edit';
import { emotions } from './emotions';
import { exit } from './exit';
import { expand } from './expand';
import { eyeClosed } from './eye-closed';
import { eyeOpen } from './eye-open';
import { facebook } from './facebook';
import { finishFlag } from './finish-flag';
import { flag } from './flag';
import { flipVideo } from './flip-video';
import { forward } from './forward';
import { forward10s } from './forward-10s';
import { gift } from './gift';
import { growth } from './growth';
import { hamburgerMenu } from './hamburger-menu';
import { happy } from './happy';
import { happyFilled } from './happy-filled';
import { healthFilled } from './health-filled';
import { healthOutlined } from './health-outlined';
import { heart } from './heart';
import { heartFilled } from './heart-filled';
import { homeFilled } from './home-filled';
import { homeOutlined } from './home-outlined';
import { hourglass } from './hourglass';
import { info } from './info';
import { journey } from './journey';
import { keyboard } from './keyboard';
import { leftArrow } from './left-arrow';
import { leftChevron } from './left-chevron';
import { lightbulb } from './lightbulb';
import { libraryFilled } from './library-filled';
import { libraryOutlined } from './library-outlined';
import { linkedin } from './linkedin';
import { liquor } from './liquor';
import { loading } from './loading';
import { lock } from './lock';
import { lungs } from './lungs';
import { mail } from './mail';
import { medicationBottle } from './medication-bottle';
import { messageFilled } from './message-filled';
import { messageOutlined } from './message-outlined';
import { mic } from './mic';
import { micOff } from './mic-off';
import { mind } from './mind';
import { minus } from './minus';
import { money } from './money';
import { multiDocument } from './multi-document';
import { noInternet } from './no-internet';
import { noSound } from './no-sound';
import { noVideo } from './no-video';
import { notification } from './notification';
import { okay } from './okay';
import { okayFilled } from './okay-filled';
import { open } from './open';
import { openNewWindow } from './open-new-window';
import { openWindow } from './open-window';
import { opioid } from './opioid';
import { pairBluetooth } from './pair-bluetooth';
import { pause } from './pause';
import { pelatoken } from './pelatoken';
import { pen } from './pen';
import { phone } from './phone';
import { piggyBank } from './piggy-bank';
import { pin } from './pin';
import { play } from './play';
import { playCircle } from './play-circle';
import { plus } from './plus';
import { prescription } from './prescription';
import { profile } from './profile';
import { profileOutlined } from './profile-outlined';
import { profileFilled } from './profile-filled';
import { prohibited } from './prohibited';
import { question } from './question';
import { radar } from './radar';
import { reallyHappy } from './really-happy';
import { reallyHappyFilled } from './really-happy-filled';
import { reallySad } from './really-sad';
import { reallySadFilled } from './really-sad-filled';
import { refresh } from './refresh';
import { reverseCamera } from './reverse-camera';
import { ribbon } from './ribbon';
import { rightArrow } from './right-arrow';
import { rightChevron } from './right-chevron';
import { sad } from './sad';
import { sadFilled } from './sad-filled';
import { search } from './search';
import { send } from './send';
import { settings } from './settings';
import { share } from './share';
import { shield } from './shield';
import { smartphone } from './smartphone';
import { soundwave } from './soundwave';
import { speaker } from './speaker';
import { speedometer } from './speedometer';
import { star } from './star';
import { starFilled } from './star-filled';
import { statsFilled } from './stats-filled';
import { statsOutlined } from './stats-outlined';
import { statusDot } from './status-dot';
import { stethoscope } from './stethoscope';
import { stimulants } from './stimulants';
import { stop } from './stop';
import { sun } from './sun';
import { synergy } from './synergy';
import { target } from './target';
import { thumbsDown } from './thumbs-down';
import { thumbsUp } from './thumbs-up';
import { thumbsUpStrong } from './thumbs-up-strong';
import { tick } from './tick';
import { tickWithContainer } from './tick-with-container';
import { tickWithOutlinedContainer } from './tick-with-outlined-container';
import { together } from './together';
import { toolbox } from './toolbox';
import { topArrow } from './top-arrow';
import { trash } from './trash';
import { trigger } from './trigger';
import { trophy } from './trophy';
import { twitter } from './twitter';
import { unlock } from './unlock';
import { upChevron } from './up-chevron';
import { vape } from './vape';
import { video } from './video';
import { videoOutlined } from './video-outlined';
import { warning } from './warning';
import { wave } from './wave';
import { whistle } from './whistle';
import { wine } from './wine';
import { youtube } from './youtube';

export const iconDefs: Record<IconName, IconDef> = {
  'backward-10s': backward10s,
  'bar-graph': barGraph,
  'beaker-filled': beakerFilled,
  'beaker-outlined': beakerOutlined,
  'bubble-speech': bubbleSpeech,
  'calendar-time': calendarTime,
  'chewing-tobacco': chewingTobacco,
  'down-arrow': downArrow,
  'down-chevron': downChevron,
  'eye-closed': eyeClosed,
  'eye-open': eyeOpen,
  'finish-flag': finishFlag,
  'flip-video': flipVideo,
  'forward-10s': forward10s,
  'hamburger-menu': hamburgerMenu,
  'happy-filled': happyFilled,
  'health-filled': healthFilled,
  'health-outlined': healthOutlined,
  'heart-filled': heartFilled,
  'home-filled': homeFilled,
  'home-outlined': homeOutlined,
  'left-arrow': leftArrow,
  'left-chevron': leftChevron,
  'library-filled': libraryFilled,
  'library-outlined': libraryOutlined,
  'medication-bottle': medicationBottle,
  'message-filled': messageFilled,
  'message-outlined': messageOutlined,
  'multi-document': multiDocument,
  'no-internet': noInternet,
  'no-sound': noSound,
  'no-video': noVideo,
  'okay-filled': okayFilled,
  'open-new-window': openNewWindow,
  'open-window': openWindow,
  'pair-bluetooth': pairBluetooth,
  'piggy-bank': piggyBank,
  'play-circle': playCircle,
  'profile-filled': profileFilled,
  'profile-outlined': profileOutlined,
  'really-happy': reallyHappy,
  'really-happy-filled': reallyHappyFilled,
  'really-sad': reallySad,
  'really-sad-filled': reallySadFilled,
  'reverse-camera': reverseCamera,
  'right-arrow': rightArrow,
  'right-chevron': rightChevron,
  'sad-filled': sadFilled,
  'star-filled': starFilled,
  'stats-filled': statsFilled,
  'stats-outlined': statsOutlined,
  'status-dot': statusDot,
  'thumbs-down': thumbsDown,
  'thumbs-up': thumbsUp,
  'thumbs-up-strong': thumbsUpStrong,
  'tick-with-container': tickWithContainer,
  'tick-with-outlined-container': tickWithOutlinedContainer,
  'top-arrow': topArrow,
  'up-chevron': upChevron,
  add: add,
  adjusting: adjusting,
  alcohol: alcohol,
  alert: alert,
  attach: attach,
  backward: backward,
  beer: beer,
  blow: blow,
  bolt: bolt,
  'bolt-filled': boltFilled,
  bookmark: bookmark,
  books: books,
  calendar: calendar,
  camera: camera,
  cannabis: cannabis,
  care: care,
  chart: chart,
  check: check,
  cigarette: cigarette,
  clock: clock,
  close: close,
  coffee: coffee,
  coin: coin,
  compass: compass,
  computer: computer,
  corporate: corporate,
  cup: cup,
  delivery: delivery,
  detach: detach,
  document: document,
  download: download,
  dropdown: dropdown,
  edit: edit,
  emotions: emotions,
  exit: exit,
  expand: expand,
  facebook: facebook,
  flag: flag,
  forward: forward,
  gift: gift,
  growth: growth,
  happy: happy,
  heart: heart,
  hourglass: hourglass,
  info: info,
  journey: journey,
  keyboard: keyboard,
  linkedin: linkedin,
  lightbulb: lightbulb,
  liquor: liquor,
  loading: loading,
  lock: lock,
  lungs: lungs,
  mail: mail,
  mic: mic,
  'mic-off': micOff,
  mind: mind,
  minus: minus,
  money: money,
  notification: notification,
  okay: okay,
  open: open,
  opioid: opioid,
  pause: pause,
  pelatoken: pelatoken,
  pen: pen,
  phone: phone,
  pin: pin,
  play: play,
  plus: plus,
  prescription: prescription,
  profile: profile,
  prohibited: prohibited,
  question: question,
  radar: radar,
  refresh: refresh,
  ribbon: ribbon,
  sad: sad,
  search: search,
  send: send,
  settings: settings,
  share: share,
  shield: shield,
  smartphone: smartphone,
  soundwave: soundwave,
  speaker: speaker,
  speedometer: speedometer,
  star: star,
  stethoscope: stethoscope,
  stimulants: stimulants,
  stop: stop,
  sun: sun,
  synergy: synergy,
  target: target,
  tick: tick,
  together: together,
  toolbox: toolbox,
  trash: trash,
  trigger: trigger,
  trophy: trophy,
  twitter: twitter,
  unlock: unlock,
  vape: vape,
  video: video,
  'video-outlined': videoOutlined,
  warning: warning,
  wave: wave,
  whistle: whistle,
  wine: wine,
  youtube: youtube,
};
